import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import {
  User,
  LogOut,
  LayoutDashboard,
  Settings,
  ShoppingCart,
  Menu,
  ChevronDown,
  LayoutGrid,
} from 'lucide-react';
import { API_URL } from '../utils/api';

const Logo = () => (
  <img
    src="/azamein-logo.png"
    alt="Azamein"
    width="254"
    height="72"
    className="h-8 sm:h-9 w-auto"
  />
);

// Nested dropdown entry for a category that has its own subcategories.
const CategoryMenuItem = ({ category, allCategories, onNavigate }) => {
  const subcategories = allCategories.filter((cat) => cat.parent_id === category.id);

  if (subcategories.length === 0) {
    return (
      <DropdownMenuItem
        onClick={() => onNavigate(`/templates?category=${category.id}`)}
        className="cursor-pointer"
      >
        {category.name}
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="cursor-pointer">{category.name}</DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-48">
        <DropdownMenuItem
          onClick={() => onNavigate(`/templates?category=${category.id}`)}
          className="cursor-pointer"
        >
          View all {category.name}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {subcategories.map((subcat) => (
          <CategoryMenuItem
            key={subcat.id}
            category={subcat}
            allCategories={allCategories}
            onNavigate={onNavigate}
          />
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
};

// Indented category tree for the mobile menu.
const MobileCategoryList = ({ parentId, categories, depth = 0, onNavigate }) => {
  const items = categories.filter((cat) => (parentId ? cat.parent_id === parentId : !cat.parent_id));
  if (items.length === 0) return null;

  return (
    <ul className={depth > 0 ? 'ml-4 border-l border-border pl-3' : ''}>
      {items.map((category) => (
        <li key={category.id}>
          <button
            type="button"
            onClick={() => onNavigate(`/templates?category=${category.id}`)}
            className={`w-full text-left py-2 px-2 rounded-md hover:bg-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold ${
              depth === 0 ? 'font-medium text-ink' : 'text-sm text-ink/70'
            }`}
          >
            {category.name}
          </button>
          <MobileCategoryList
            parentId={category.id}
            categories={categories}
            depth={depth + 1}
            onNavigate={onNavigate}
          />
        </li>
      ))}
    </ul>
  );
};

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { cartCount, refreshCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [categoriesStatus, setCategoriesStatus] = useState('loading');
  const [mobileOpen, setMobileOpen] = useState(false);
  // The menu frame's gradient slides a little toward the mouse (left ↔ right).
  const [framePos, setFramePos] = useState(50);
  const onFrameMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const fx = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    const next = Math.round(75 - fx * 50);
    if (next !== framePos) setFramePos(next);
  };

  // A soft blurred glow inside the frame follows the mouse left/right across the whole screen:
  // mouse at the screen's right edge -> glow at the bar's right end, centred -> centred.
  // Up/down movement is ignored. Written straight to a CSS variable (no re-render per move).
  const navRef = useRef(null);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    let frame = 0;
    let lastX = window.innerWidth / 2;
    const apply = () => {
      frame = 0;
      const nav = navRef.current;
      if (!nav) return;
      const fx = Math.min(1, Math.max(0, lastX / Math.max(1, window.innerWidth)));
      nav.style.setProperty('--glow-x', `${(fx * 100).toFixed(2)}%`);
    };
    const onMove = (e) => {
      lastX = e.clientX;
      if (!frame) frame = requestAnimationFrame(apply);
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    apply();
    return () => {
      window.removeEventListener('pointermove', onMove);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/categories`)
      .then((response) => {
        if (cancelled) return;
        setCategories(Array.isArray(response.data) ? response.data : []);
        setCategoriesStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setCategoriesStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep the cart badge in sync after signing in or out.
  useEffect(() => {
    if (user) refreshCart();
  }, [user, refreshCart]);

  // Close the mobile menu whenever the route changes.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  const handleLogout = () => {
    logout();
    setMobileOpen(false);
    navigate('/');
  };

  const go = (path) => {
    setMobileOpen(false);
    navigate(path);
  };

  const mainCategories = categories.filter((cat) => !cat.parent_id);
  const showCategoryLinks = categoriesStatus === 'ready' && mainCategories.length > 0;

  const desktopLinkClass =
    'nav-link px-3.5 py-2 rounded-full text-ink/80 hover:text-ink hover:bg-cream transition-colors font-semibold text-[15px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold';

  return (
    <header className="fixed top-0 left-0 right-0 z-50 px-3 sm:px-6 pt-3 pointer-events-none">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 focus:z-50 focus:bg-white focus:px-3 focus:py-2 focus:rounded focus:shadow"
      >
        Skip to content
      </a>
      <nav
        ref={navRef}
        className="relative pointer-events-auto max-w-7xl mx-auto rounded-full backdrop-blur-md shadow-[0_12px_30px_-18px_rgba(26,26,26,0.35)] pl-4 pr-2 sm:pl-6 sm:pr-2.5 py-2"
        onMouseMove={onFrameMove}
        onMouseLeave={() => setFramePos(50)}
        style={{
          border: '1.5px solid transparent',
          background:
            'linear-gradient(rgba(255,255,255,0.88), rgba(255,255,255,0.88)) padding-box, ' +
            'linear-gradient(90deg, #72D7F5, #F28AC6 25%, #EC008C 50%, #0CBFF2 75%, #F28AC6) border-box',
          backgroundSize: '100% 100%, 220% 100%',
          backgroundPosition: `0 0, ${framePos}% 0`,
          transition: 'background-position 0.45s ease-out',
        }}
        aria-label="Main"
      >
        {/* moving glow (see navRef effect); clipped to the pill, behind the menu items */}
        <span className="nav-glow-track" aria-hidden="true">
          <span className="nav-glow" />
        </span>
        <div className="relative flex items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="nav-logo"
            aria-label="Azamein home"
            className="shrink-0 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
          >
            <Logo />
          </Link>

          {/* Desktop category navigation */}
          <div className="hidden lg:flex items-center gap-1 min-w-0">
            {categoriesStatus === 'loading' && (
              <div className="flex gap-6" aria-hidden="true">
                {[0, 1, 2, 3].map((i) => (
                  <span key={i} className="h-4 w-20 rounded bg-ink/10 animate-pulse" />
                ))}
              </div>
            )}

            {!showCategoryLinks && categoriesStatus !== 'loading' && (
              <NavLink to="/templates" className={desktopLinkClass}>
                Browse Templates
              </NavLink>
            )}

            {showCategoryLinks &&
              mainCategories.map((category) => {
                const subcategories = categories.filter((cat) => cat.parent_id === category.id);

                if (subcategories.length === 0) {
                  return (
                    <Link
                      key={category.id}
                      to={`/templates?category=${category.id}`}
                      className={desktopLinkClass}
                      data-testid={`nav-category-${category.id}`}
                    >
                      {category.name}
                    </Link>
                  );
                }

                return (
                  <DropdownMenu key={category.id}>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className={`${desktopLinkClass} inline-flex items-center gap-1`}
                        data-testid={`nav-category-${category.id}`}
                      >
                        {category.name}
                        <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56">
                      <DropdownMenuItem
                        onClick={() => go(`/templates?category=${category.id}`)}
                        className="cursor-pointer"
                      >
                        View all {category.name}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {subcategories.map((subcat) => (
                        <CategoryMenuItem
                          key={subcat.id}
                          category={subcat}
                          allCategories={categories}
                          onNavigate={go}
                        />
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })}
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {user && (
              <Button asChild variant="ghost" size="icon" className="relative">
                <Link
                  to="/cart"
                  data-testid="nav-cart"
                  aria-label={cartCount > 0 ? `Cart, ${cartCount} item${cartCount === 1 ? '' : 's'}` : 'Cart'}
                >
                  <ShoppingCart className="h-5 w-5" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-pink text-white text-[11px] min-w-5 h-5 px-1 rounded-full flex items-center justify-center font-semibold">
                      {cartCount}
                    </span>
                  )}
                </Link>
              </Button>
            )}

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="hidden sm:flex items-center gap-2 rounded-full gradient-border gb-glass hover:bg-cream"
                    data-testid="user-menu-trigger"
                  >
                    <User className="h-4 w-4" />
                    <span className="max-w-[10rem] truncate">{user.name || 'Account'}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  {user.email && (
                    <>
                      <DropdownMenuLabel className="font-normal text-xs text-ink/60 truncate">
                        {user.email}
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={() => go('/dashboard')} data-testid="menu-dashboard">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    My Invitations
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem onClick={() => go('/admin')} data-testid="menu-admin">
                      <Settings className="mr-2 h-4 w-4" />
                      Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Button asChild variant="ghost" className="text-ink font-semibold rounded-full">
                  <Link to="/login" state={{ from: location }} data-testid="nav-login">
                    Sign In
                  </Link>
                </Button>
                <Button asChild className="bg-ink text-paper hover:bg-ink/90 rounded-full h-11 px-5 font-semibold">
                  <Link to="/register" state={{ from: location }} data-testid="nav-register">
                    Get started
                  </Link>
                </Button>
              </div>
            )}

            {/* Mobile / tablet menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden rounded-full bg-ink text-paper hover:bg-ink/90 hover:text-paper"
                  aria-label="Open menu"
                  data-testid="nav-mobile-menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-paper w-[85vw] max-w-sm overflow-y-auto p-0">
                <SheetHeader className="px-5 pt-5 pb-3 gradient-hairline-b text-left">
                  <SheetTitle className="font-serif text-xl text-ink">Menu</SheetTitle>
                </SheetHeader>

                <div className="px-3 py-4 space-y-6">
                  <div>
                    <button
                      type="button"
                      onClick={() => go('/templates')}
                      className="w-full flex items-center gap-2 py-2 px-2 rounded-md font-medium text-ink hover:bg-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                    >
                      <LayoutGrid className="h-4 w-4 text-gold" aria-hidden="true" />
                      All templates
                    </button>
                  </div>

                  {showCategoryLinks && (
                    <div>
                      <p className="px-2 mb-1 text-xs font-medium uppercase tracking-wide text-ink/50">
                        Occasions
                      </p>
                      <MobileCategoryList categories={categories} onNavigate={go} />
                    </div>
                  )}

                  <div className="border-t border-border pt-4">
                    {user ? (
                      <ul className="space-y-1">
                        <li className="px-2 pb-2 text-sm text-ink/60 truncate">
                          Signed in as <span className="font-medium text-ink">{user.name || user.email}</span>
                        </li>
                        <li>
                          <button type="button" onClick={() => go('/dashboard')} className="w-full flex items-center gap-2 py-2 px-2 rounded-md hover:bg-cream">
                            <LayoutDashboard className="h-4 w-4" aria-hidden="true" /> My Invitations
                          </button>
                        </li>
                        <li>
                          <button type="button" onClick={() => go('/cart')} className="w-full flex items-center gap-2 py-2 px-2 rounded-md hover:bg-cream">
                            <ShoppingCart className="h-4 w-4" aria-hidden="true" /> Cart
                            {cartCount > 0 && <span className="ml-auto text-xs bg-pink text-white rounded-full px-2 py-0.5">{cartCount}</span>}
                          </button>
                        </li>
                        {user.is_admin && (
                          <li>
                            <button type="button" onClick={() => go('/admin')} className="w-full flex items-center gap-2 py-2 px-2 rounded-md hover:bg-cream">
                              <Settings className="h-4 w-4" aria-hidden="true" /> Admin Panel
                            </button>
                          </li>
                        )}
                        <li>
                          <button type="button" onClick={handleLogout} className="w-full flex items-center gap-2 py-2 px-2 rounded-md hover:bg-cream text-ink/80">
                            <LogOut className="h-4 w-4" aria-hidden="true" /> Sign out
                          </button>
                        </li>
                      </ul>
                    ) : (
                      <div className="grid gap-2 px-2">
                        <Button asChild className="w-full bg-ink text-paper hover:bg-ink/90 rounded-full">
                          <Link to="/register" state={{ from: location }}>Get started</Link>
                        </Button>
                        <Button asChild variant="outline" className="w-full rounded-full gradient-border gb-paper">
                          <Link to="/login" state={{ from: location }}>Sign in</Link>
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
};
